import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { PageHero, ContentSection, SEOContent, CTASection } from '../components/shared';
import { pageHeroImages } from '../data/mock';
import { ArrowRight, ArrowDown } from 'lucide-react';

const dmpFeatures = [
  {
    id: 'diabetes',
    title: 'Diabetes mellitus Typ 2',
    description: 'Strukturierte Betreuung bei Diabetes für bessere Blutzuckerkontrolle.',
    image: '/images/Content_Behandlung_1.webp'
  },
  {
    id: 'khk',
    title: 'Koronare Herzkrankheit (KHK)',
    description: 'Kontinuierliche Begleitung bei Herzerkrankungen.',
    image: '/images/Kardiologie 2.webp'
  },
  {
    id: 'asthma',
    title: 'Asthma bronchiale',
    description: 'Regelmäßige Kontrollen für freies Atmen.',
    image: '/images/Content_Behandlung_7.webp'
  },
  {
    id: 'copd',
    title: 'COPD',
    description: 'Strukturierte Versorgung bei chronischer Lungenerkrankung.',
    image: '/images/Content_Behandlung_8.webp'
  }
];

export const DMPPage = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Layout>
      <PageHero
        title="DMP – Chronikerprogramme im Medicum Diez"
        subtitle="Strukturierte Betreuung bei chronischen Erkrankungen – persönlich und kontinuierlich."
        backgroundImage={pageHeroImages.dmp}
        ctaText="DMP-Termin buchen"
      />

      {/* Schnellnavigation - Anchor Buttons */}
      <section className="py-6 bg-[#032925]">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => scrollToSection('programme')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Unsere Programme
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('diabetes')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Diabetes
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('khk')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              KHK
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('asthma')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Asthma
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('copd')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              COPD
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('termin')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Termin buchen
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Feature Grid mit klickbaren Karten */}
      <section id="programme" className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-[#032925]">
              Unsere DMP-Programme
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {dmpFeatures.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#8ae9bb]/50 rounded-2xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-all"
                onClick={() => scrollToSection(feature.id)}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#032925] mb-2">{feature.title}</h3>
                  <p className="text-[#032925]/70 text-sm mb-4">{feature.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#032925] hover:text-[#1a1308]">
                    Mehr erfahren
                    <ArrowDown className="w-4 h-4" />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Diabetes Content */}
      <div id="diabetes">
        <ContentSection
          title="DMP Diabetes mellitus Typ 2"
          content="Das Disease-Management-Programm für Diabetes mellitus Typ 2 unterstützt Sie bei der Kontrolle Ihres Blutzuckers durch regelmäßige Untersuchungen, individuelle Schulungen und eine enge ärztliche Begleitung. Gemeinsam arbeiten wir an der Optimierung Ihrer Therapie und der Vermeidung von Folgeerkrankungen."
          image="/images/Content_Behandlung_1.webp"
          backgroundColor="white"
        />
      </div>

      {/* KHK Content */}
      <div id="khk">
        <ContentSection
          title="DMP Koronare Herzkrankheit (KHK)"
          content="Bei koronarer Herzkrankheit begleiten wir Sie mit strukturierten Kontrollen, Medikamentenmanagement und Lebensstilberatung. Ziel ist es, Ihre Herzgesundheit zu stabilisieren, Beschwerden zu lindern und das Risiko für Herzinfarkt und andere Komplikationen zu reduzieren."
          image="/images/Kardiologie 2.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* Asthma Content */}
      <div id="asthma">
        <ContentSection
          title="DMP Asthma bronchiale"
          content="Das Asthma-Programm hilft Ihnen, Ihre Atemwegsbeschwerden besser zu kontrollieren. Durch regelmäßige Lungenfunktionsmessungen, Inhalationsschulungen und individuelle Therapieanpassungen verbessern wir Ihre Lebensqualität und reduzieren akute Anfälle."
          image="/images/Content_Behandlung_7.webp"
          backgroundColor="white"
        />
      </div>

      {/* COPD Content */}
      <div id="copd">
        <ContentSection
          title="DMP COPD"
          content="Bei chronisch obstruktiver Lungenerkrankung (COPD) bieten wir strukturierte Betreuung mit Fokus auf Atemtherapie, Bewegungsförderung und Medikamentenoptimierung. Ziel ist die Verlangsamung des Krankheitsverlaufs und die Verbesserung Ihrer Belastbarkeit im Alltag."
          image="/images/Content_Behandlung_8.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* Strukturierte Versorgung */}
      <ContentSection
        title="Strukturierte Versorgung für mehr Sicherheit"
        content="Disease-Management-Programme unterstützen Patientinnen und Patienten mit chronischen Erkrankungen durch regelmäßige Kontrollen, strukturierte Behandlungspläne und eine enge ärztliche Begleitung. Sie profitieren von koordinierter Versorgung und klaren Behandlungszielen."
        image="/images/Content_Behandlung_2.webp"
        backgroundColor="white"
      />

      {/* Zusammenarbeit */}
      <section className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-[#032925]">
                Enge Zusammenarbeit mit Fachärzten
              </h2>
              <p className="text-[#032925]/80 leading-relaxed mb-6">
                Bei Bedarf binden wir Kardiologie, Diagnostik und weitere Fachbereiche direkt in Ihre Betreuung ein – für eine abgestimmte und kontinuierliche Behandlung. Im Medicum Diez profitieren Sie von kurzen Wegen und schneller Abstimmung zwischen den Fachrichtungen.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/leistungen/kardiologie" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                  Kardiologie & Herz-Check
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/leistungen/diagnostik" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                  Diagnostik-Zentrum
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/images/4_Termin_Beratung.webp"
                  alt="Beratung"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SEOContent
        title="DMP-Programme in Diez und Limburg"
        content="Im Medicum Diez bieten wir Disease-Management-Programme für Diabetes mellitus Typ 2, koronare Herzkrankheit, Asthma bronchiale und COPD. Unsere Hausärzte begleiten Sie strukturiert und langfristig im Rahmen der gesetzlichen Chronikerprogramme im Rhein-Lahn-Kreis."
      />

      <div id="termin">
        <CTASection
          title="DMP-Termin vereinbaren"
          subtitle="Vereinbaren Sie Ihren Termin zur Aufnahme oder Kontrolle im Chronikerprogramm online oder telefonisch."
          buttonText="DMP-Termin buchen"
        />
      </div>
    </Layout>
  );
};

export default DMPPage;
